"use client";

import { useEffect } from "react";
import { isMobileDevice } from "@/lib/utils";
import "./intercom-mobile.css";

interface IntercomSettings {
  api_base: string;
  app_id: string;
  user_id?: string;
  name?: string;
  email?: string;
  created_at?: number;
}

declare global {
  interface Window {
    Intercom?: (command: string, data?: any) => void;
    intercomSettings?: IntercomSettings;
  }
}

interface IntercomProviderProps {
  appId: string;
  userId?: string;
  userName?: string;
  userEmail?: string;
  userCreatedAt?: Date | string;
  children: React.ReactNode;
}

export function IntercomProvider({
  appId,
  userId,
  userName,
  userEmail,
  userCreatedAt,
  children,
}: IntercomProviderProps) {
  useEffect(() => {
    // Check if Intercom is enabled via environment variable
    const isEnabled = process.env.NEXT_PUBLIC_INTERCOM_ENABLED === "true";


    if (!isEnabled) {
      return;
    }


    // Load Intercom script first (SPA method from official docs)
    const w = window;
    const ic = w.Intercom;

    if (typeof ic === "function") {
      // If already loaded, boot with user data and hide launcher
      ic("boot", {
        api_base: "https://api-iam.intercom.io",
        app_id: appId,
        user_id: userId,
        name: userName,
        email: userEmail,
        created_at: userCreatedAt
          ? Math.floor(new Date(userCreatedAt).getTime() / 1000)
          : undefined,
        hide_default_launcher: true, // Hide the default launcher
        alignment: "right",
        horizontal_padding: 0,
        vertical_padding: 0,
      });

      // Add event listeners for show/hide
      // window.Intercom("onShow", () => {
      //   document.body.classList.add("intercom-open");
      // });
      // window.Intercom("onHide", () => {
      //   document.body.classList.remove("intercom-open");
      // });
    } else {
      const d = document;
      const i: any = function () {
        i.c(arguments);
      };
      i.q = [];
      i.c = function (args: any) {
        i.q.push(args);
      };
      w.Intercom = i;

      const script = d.createElement("script");
      script.type = "text/javascript";
      script.async = true;
      script.src = `https://widget.intercom.io/widget/${appId}`;

      script.onload = () => {

        // Wait a bit for Intercom to fully initialize
        setTimeout(() => {
          if (window.Intercom && typeof window.Intercom === "function") {
            window.Intercom("boot", {
              api_base: "https://api-iam.intercom.io",
              app_id: appId,
              user_id: userId,
              name: userName,
              email: userEmail,
              created_at: userCreatedAt
                ? Math.floor(new Date(userCreatedAt).getTime() / 1000)
                : undefined,
              hide_default_launcher: true, // Hide the default launcher
              alignment: "right",
              horizontal_padding: 0,
              vertical_padding: 0,
            });

            // Add event listeners for show/hide
            window.Intercom("onShow", () => {
              document.body.classList.add("intercom-open");
            });
            window.Intercom("onHide", () => {
              document.body.classList.remove("intercom-open");
            });
          } else {
            console.error("[Intercom] window.Intercom not available after script load");
          }
        }, 100);
      };

      script.onerror = (error) => {
        console.error("[Intercom] Failed to load script:", error);
      };

      const firstScript = d.getElementsByTagName("script")[0];
      if (firstScript && firstScript.parentNode) {
        firstScript.parentNode.insertBefore(script, firstScript);
      } else {
        d.head.appendChild(script);
      }
    }

    // Cleanup function to shutdown Intercom when component unmounts
    return () => {
      if (window.Intercom) {
        window.Intercom("shutdown");
      }
    };
  }, [appId, userId, userName, userEmail, userCreatedAt]);

  return <>{children}</>;
}


// Helper function to open Intercom messenger
export function openIntercom() {
  if (typeof window !== "undefined" && window.Intercom && typeof window.Intercom === "function") {
    const isMobile = isMobileDevice();

    try {
      // On mobile, use showNewMessage for full-screen experience
      // On desktop, use show for standard messenger
      if (isMobile) {
        window.Intercom("showNewMessage");
      } else {
        window.Intercom("show");
      }

      // Listen for when the messenger is closed
      window.Intercom("onHide", () => {
      });
    } catch (error) {
      console.error("[Intercom] Error opening messenger:", error);
    }
  } else {
    console.error("[Intercom] Cannot open - Intercom not loaded yet");
  }
}

// Helper function to hide Intercom messenger
export function hideIntercom() {
  if (typeof window !== "undefined" && window.Intercom && typeof window.Intercom === "function") {
    window.Intercom("hide");
  }
}

// Helper function to show help articles
export function showIntercomHelp() {
  if (typeof window !== "undefined" && window.Intercom && typeof window.Intercom === "function") {
    window.Intercom("showArticles");
  }
}

// Helper function to update Intercom (call when route changes)
export function updateIntercom() {
  if (typeof window !== "undefined" && window.Intercom && typeof window.Intercom === "function") {
    window.Intercom("update");
  }
}

// Helper function to shutdown Intercom (call on logout)
export function shutdownIntercom() {
  if (typeof window !== "undefined" && window.Intercom && typeof window.Intercom === "function") {
    window.Intercom("shutdown");
  }
}

// Debug helper to check Intercom status
export function checkIntercomStatus() {
  if (typeof window === "undefined") {
    return;
  }


  // Try to get Intercom's internal state
  if (window.Intercom && typeof window.Intercom === "function") {
    try {
      // This will log Intercom's current state
      window.Intercom("getVisitorId");
    } catch (error) {
      console.error("[Intercom Status] Intercom exists but error:", error);
    }
  } else {
    console.error("[Intercom Status] Intercom not loaded");
  }
}
